import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import {
  FadeIn, FadeInX, StaggerContainer, StaggerItem,
  RevealLine, PageTransition,
} from "@/components/site/Animations";
import { SectionHeader } from "@/components/site/SectionHeader";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: "", email: "", organization: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Please enter a valid email address";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name.trim(), email: formData.email.trim(),
        organization: formData.organization.trim(), message: formData.message.trim(),
      });
      if (response.data.success) {
        setSubmitted(true);
        toast.success("Message sent successfully", { description: "Thank you for contacting Cortes Industries. We will respond promptly." });
        setFormData({ name: "", email: "", organization: "", message: "" });
      }
    } catch (error) {
      toast.error("Failed to send message", { description: "Please try again or contact us directly via email." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <PageTransition>
      <div data-testid="contact-page">
        <section className="bg-white pt-10 sm:pt-12 lg:pt-16 pb-10">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <FadeIn>
              <SectionHeader
                eyebrow="Get in Touch"
                title="Contact"
                description="Cortes Industries supports complex procurement environments and mission-critical programs. Contact us to explore how we can support your objectives."
              />
            </FadeIn>
          </div>
        </section>

        <RevealLine />

        <section className="py-14 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <FadeIn className="lg:col-span-7" delay={0.1}>
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                    className="text-center py-12 px-6 border border-border rounded-lg bg-secondary/30"
                    data-testid="contact-form-success-toast"
                  >
                    <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <Send className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2" style={{ fontFamily: 'var(--font-display)' }}>Message Received</h3>
                    <p className="text-sm text-muted-foreground mb-6">Thank you for contacting Cortes Industries. We will respond promptly.</p>
                    <button onClick={() => setSubmitted(false)} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-[hsl(218,60%,10%)] text-white rounded-md hover:bg-[hsl(218,60%,14%)] transition-colors btn-press">
                      Send Another Message <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: 'var(--font-display)' }}>Name <span className="text-destructive">*</span></label>
                        <Input type="text" value={formData.name} onChange={handleChange("name")} placeholder="Full name" data-testid="contact-form-name-input" className={`bg-white focus-gold ${errors.name ? 'border-destructive' : 'border-border'}`} />
                        {errors.name && <p className="text-xs text-destructive mt-1" data-testid="contact-form-error-text">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: 'var(--font-display)' }}>Email <span className="text-destructive">*</span></label>
                        <Input type="email" value={formData.email} onChange={handleChange("email")} placeholder="email@organization.com" data-testid="contact-form-email-input" className={`bg-white focus-gold ${errors.email ? 'border-destructive' : 'border-border'}`} />
                        {errors.email && <p className="text-xs text-destructive mt-1" data-testid="contact-form-error-text">{errors.email}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: 'var(--font-display)' }}>Organization</label>
                      <Input type="text" value={formData.organization} onChange={handleChange("organization")} placeholder="Organization name (optional)" data-testid="contact-form-organization-input" className="bg-white focus-gold" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" style={{ fontFamily: 'var(--font-display)' }}>Message <span className="text-destructive">*</span></label>
                      <textarea value={formData.message} onChange={handleChange("message")} placeholder="How can Cortes Industries support your program requirements?" rows={5} data-testid="contact-form-message-textarea" className={`flex w-full rounded-md border px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(41,47%,55%)] focus-visible:ring-offset-2 bg-white resize-none ${errors.message ? 'border-destructive' : 'border-border'}`} />
                      {errors.message && <p className="text-xs text-destructive mt-1" data-testid="contact-form-error-text">{errors.message}</p>}
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-xs text-muted-foreground">All inquiries are treated confidentially. We typically respond within one business day.</p>
                      <button type="submit" disabled={isSubmitting} data-testid="contact-form-submit-button" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-[hsl(218,60%,10%)] text-white rounded-md hover:bg-[hsl(218,60%,14%)] transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0 btn-press">
                        {isSubmitting ? "Sending..." : "Send Message"} <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                )}
              </FadeIn>

              <FadeInX className="lg:col-span-5" x={40} delay={0.25}>
                <div className="border border-border rounded-lg p-6 bg-white card-hover">
                  <h3 className="text-lg font-semibold tracking-tight text-foreground mb-5" style={{ fontFamily: 'var(--font-display)' }}>Direct Contact</h3>
                  <div className="space-y-5">
                    <a href="mailto:contact@cortesindustries.com" className="flex items-start gap-4 group">
                      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[hsl(41,47%,61%)]" /></div>
                      <div>
                        <p className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground mb-0.5" style={{ fontFamily: 'var(--font-display)' }}>Email</p>
                        <p className="text-sm text-foreground group-hover:text-[hsl(41,47%,61%)] transition-colors">contact@cortesindustries.com</p>
                      </div>
                    </a>
                    <Separator />
                    <a href="tel:+12022022552" className="flex items-start gap-4 group">
                      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-[hsl(41,47%,61%)]" /></div>
                      <div>
                        <p className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground mb-0.5" style={{ fontFamily: 'var(--font-display)' }}>Phone</p>
                        <p className="text-sm text-foreground group-hover:text-[hsl(41,47%,61%)] transition-colors">+1-202-202-2552</p>
                      </div>
                    </a>
                    <Separator />
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[hsl(41,47%,61%)]" /></div>
                      <div>
                        <p className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground mb-0.5" style={{ fontFamily: 'var(--font-display)' }}>Location</p>
                        <p className="text-sm text-foreground">Salt Lake City, Utah<br />United States</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-5 bg-secondary/60 rounded-lg border border-border">
                  <p className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground mb-2" style={{ fontFamily: 'var(--font-display)' }}>Confidentiality</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">All communications are treated as confidential. Cortes Industries respects the sensitivity of procurement and program-related discussions.</p>
                </div>
              </FadeInX>
            </div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
}
